import type { ReactElement } from 'react';
import { useState } from 'react';
import { Copy } from 'lucide-react';
import { api } from '../api';
import { usePortalSession, writeStoredSession } from '../auth';

export function UserProfile(): ReactElement {
  const { session, refresh, loading } = usePortalSession();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!session) {
    return (
      <p className="text-slate-300">
        You are not signed in. Visit{' '}
        <a className="text-accent" href="/login">
          sign in
        </a>{' '}
        to continue.
      </p>
    );
  }

  const rotate = async (): Promise<void> => {
    setError(null);
    try {
      const result = await api.regenerateApiKey();
      setRevealed(result.api_key);
      writeStoredSession({ ...session, apiKey: result.api_key });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to rotate');
    }
  };

  const copyApiKey = (): void => {
    const key = revealed ?? session.apiKey ?? session.apiKeyMasked;
    if (key) void navigator.clipboard.writeText(key);
  };

  return (
    <section className="max-w-xl bg-panel rounded p-6 space-y-4">
      <h1 className="text-lg font-semibold">Profile</h1>
      <div>
        <div className="text-xs text-slate-400">Email</div>
        <div>{session.email}</div>
      </div>
      <div>
        <div className="text-xs text-slate-400">Display Name</div>
        <div>{session.displayName ?? '—'}</div>
      </div>
      <div>
        <div className="text-xs text-slate-400">API Key</div>
        <div className="flex items-center gap-2">
          <div className="font-mono text-sm break-all">
            {revealed ?? session.apiKey ?? session.apiKeyMasked}
          </div>
          <button
            type="button"
            onClick={copyApiKey}
            className="p-1.5 rounded bg-slate-800 text-slate-300 hover:text-accent transition-colors shrink-0"
            title="Copy API key"
          >
            <Copy size={16} />
          </button>
        </div>
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={() => void rotate()}
            className="px-3 py-1 rounded bg-slate-800 text-sm"
            disabled={loading}
          >
            Regenerate
          </button>
        </div>
        {error && <p className="text-rose-400 text-sm mt-2">{error}</p>}
      </div>
      <p className="text-xs text-slate-500">
        Use the API key as{' '}
        <code className="bg-slate-900 px-1 rounded">
          Authorization: Bearer &lt;api_key&gt;
        </code>{' '}
        against the MCP Streamable HTTP endpoint at{' '}
        <code className="bg-slate-900 px-1 rounded">/messages</code>.
      </p>
    </section>
  );
}
